/* eslint-disable no-console, no-restricted-syntax */

import type { LogLevel } from '@luna/env';
import { getEnv } from '@luna/env';

import type { LoggerFunction } from './type';
import { getLoggerMethods } from './initCustomLogger';

const EMOJI_CODE = { FIRE: 0x1f525, WATER: 0x1f4a7 };
const fire: string = String.fromCodePoint(EMOJI_CODE.FIRE);
const water: string = String.fromCodePoint(EMOJI_CODE.WATER);

const logLevelValueArray: LogLevel[] = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'NONE'];

const isSeverityEnabled = (logLevel: LogLevel): boolean => {
    const envMaxLogLevel = getEnv()?.logLevel;
    if (!envMaxLogLevel) {
        return __DEV__;
    }

    const logLevelValue = logLevelValueArray.indexOf(logLevel);
    const envMaxLogLevelValue = logLevelValueArray.indexOf(envMaxLogLevel);

    return logLevelValue >= envMaxLogLevelValue;
};

const getPrefix = (severity: LogLevel): string => {
    return `[${new Date().toISOString()}]\n[${severity}]`;
};

const error: LoggerFunction = (message?: unknown, ...optionalParams: unknown[]): void => {
    if (!isSeverityEnabled('ERROR')) {
        return;
    }
    getLoggerMethods().error(message, ...optionalParams);
    console.error(getPrefix('ERROR'), message, ...optionalParams);
};
const warn: LoggerFunction = (message?: unknown, ...optionalParams: unknown[]): void => {
    if (!isSeverityEnabled('WARN')) {
        return;
    }
    getLoggerMethods().warn(message, ...optionalParams);
    console.warn(getPrefix('WARN'), message, ...optionalParams);
};
const info: LoggerFunction = (message?: unknown, ...optionalParams: unknown[]): void => {
    if (!isSeverityEnabled('INFO')) {
        return;
    }
    getLoggerMethods().info(message, ...optionalParams);
    console.info(getPrefix('INFO'), message, ...optionalParams);
};
const debug: LoggerFunction = (message?: unknown, ...optionalParams: unknown[]): void => {
    if (!isSeverityEnabled('DEBUG')) {
        return;
    }
    getLoggerMethods().debug(message, ...optionalParams);
    console.debug(getPrefix('DEBUG'), message, ...optionalParams);
};
const trace: LoggerFunction = (message?: unknown, ...optionalParams: unknown[]): void => {
    if (!isSeverityEnabled('TRACE')) {
        return;
    }
    getLoggerMethods().trace(message, ...optionalParams);
    console.trace(getPrefix('TRACE'), message, ...optionalParams);
};

/**
 * Useful when its required to track some logging in the development mode.
 * Can be searched&tracked by prefix [DEV]
 */
const dev: LoggerFunction = (message?: unknown, ...optionalParams: unknown[]): void => {
    if (!__DEV__) {
        return;
    }
    console.log(
        `%c${fire}[DEV]${water} ${getPrefix('INFO')}`,
        'color: #33ff33;',
        message,
        optionalParams,
    );
};

/**
 * Logger with various log levels and environment-aware logging functions.
 *
 * @namespace
 * @property {LoggerFunction} error - Logs error messages and captures exceptions.
 * @property {LoggerFunction} warn - Logs warning messages.
 * @property {LoggerFunction} info - Logs informational messages.
 * @property {LoggerFunction} debug - Logs debug messages.
 * @property {LoggerFunction} trace - Logs trace messages with stack traces.
 * @property {LoggerFunction} dev - Logs development-only messages with distinct styling.
 *
 * @example
 * import { logger } from '@luna/logger';
 *
 * logger.info('Session changed event', event);
 * logger.error('An error occurred', new Error('Something went wrong'));
 * logger.dev('Debug information', { someData: 'value' });
 *
 * @remarks
 * - Log levels are controlled by the environment's logLevel setting.
 * - In production, lower severity logs may be disabled for performance.
 * - The 'dev' logger only outputs in development environments.
 * - Error logs are automatically captured by Sentry for error tracking.
 */
export const logger = {
    error,
    trace,
    warn,
    info,
    debug,
    dev,
};
